import { message, Modal } from 'antd'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ActionArea from '../../components/ActionArea'
import Head from '../../components/Head'
import List from '../../components/List'
import SelectInput from '../../components/UI/SelectInput'
import SmartSearch from '../../components/UI/SmartSearch'
import {getPermission} from '../../services/permisssion'
import locationsStatus from '../../utils/locationsStatus'
import direction from '../../utils/filterDirectionOrder'
import permissionType from '../../utils/permissionType'
import { getLocationsList, updateLocationStatus } from '../../services/locations'
import Button from '../../components/UI/Button'
import {
  EditOutlined,
} from '@ant-design/icons'
import { notificationError } from '../../services/notification'
import { useLoading } from '../../context/LoadingProvider/useLoading'
import { getNextStatusValue } from '../../utils/status'
import TableStatus from '../../components/UI/TableStatus'
import { useListRecord } from '../../context/ListRecordProvider/useListRecord'
import { IParams } from '../../context/ListRecordProvider/types'
import { sendSystemLog, useAuth } from '../../context/AuthProvider/useAuth'
import { stringCompare } from '../../utils/sorters'

const selectOptions = [
  {name: 'All', value: 'All'},
  {name: 'Active', value: locationsStatus.active},
  {name: 'Inactive', value: locationsStatus.inactive},
  {name: 'Closed', value: locationsStatus.closed},
]

function Locations(props: {accessLevel: string}) {
  const history = useNavigate()
  const {canEdit} = useAuth()
  const listRecord = useListRecord()
  const record = listRecord.getRecord('locationsList')
  const loading = useLoading()
  const urlParams = new URLSearchParams(window.location.search);
  const useRecord = urlParams.get('useRecord')
  const [canClosePermission, setCanClosePermission] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [locationToPreview, setLocationToPreview] = useState<any>("")
  const [previewModal, setPreviewModal] = useState(false)
  const [paramsToChangeStatus, setParamsToChangeStatus] = useState({id: '', status: ''})
  const [search, setSearch] = useState('')
  const [params, setParams] = useState<IParams>({
    status: selectOptions[0].value,
    fieldOrder: '',
    directionOrder: '',
    id: null,
    name: '',
    type: '',
  })
  const [validSearch, setValidSearch] = useState(false)
  const [locationsList, setLocationsList] = useState({
    count: 0,
    page: null,
    perpage: 0,
    list: [],
  })
  const [changeStatusMessage, setChangeStatusMessage] = useState('The selected location will change. Are you sure?')
  const log = {
    log_type: 'visited',
    log_object: 'locations'
  }

  useEffect(() => {
    sendSystemLog(log);
  }, [])

  useEffect(() => {
    (async() =>{
      loading.addLoading()
      const canClosePermission = await getPermission('location_close')
      setCanClosePermission(canClosePermission === permissionType.manage)

      if(record && useRecord) {
        const response = await getLocationsList({
          status: record.status,
          [`${record.type}Id`]: record.id,
        })
        setLocationsList(response)
        setParams(record)
        setSearch(record.name)
      }
      loading.removeLoading()
    })()
  }, [])


  const onRefresh = async (order?: string, directionparam?: string) => {
    if(!validSearch || !params.id) return 
    loading.addLoading()
    let requestParams = {
      status: params.status,
      [`${params.type}Id`]: params.id,
      fieldOrder: '',
      directionOrder: ''
    }
    
    const response = await getLocationsList(requestParams)
    loading.removeLoading()
    listRecord.addRecord('locationsList', params)
    return setLocationsList(response)

  }


  const getStatusLabel = (status: string) => {
    if(status === 'true') {
      return <div className="cashiers__table__status cashiers__table__status--active">Yes</div>
    } else {
      return <div className="cashiers__table__status cashiers__table__status--inactive">No</div>
    }
  }

  const getActionButtons = (value: any) => (
    <div className="cashiers__table__actions">
      <Button 
        onClick={() =>{
          setLocationToPreview(value)
          setPreviewModal(true)
        }}
        style={{backgroundColor: '#73a839'}}
        type='primary'
      >
        <p>Details</p>
      </Button>
      <Button 
        onClick={() => openChangeStatusModal(value.id, value.status)}
        style={{backgroundColor: getNextStatusValue(value.status, 'color'), width: 91}}
        type='primary'
        disabled={!canEdit && !canClosePermission}
      >
        <p>{getNextStatusValue(value.status, 'label')}</p>
      </Button>
      <Button 
        onClick={() => history(`../locations/new/${value.id}`)}
        type='primary'
        style={{backgroundColor: '#2fa4e7'}}
        disabled={!canEdit}
      >
        <EditOutlined style={{ fontSize: '16px', color: '#FFF' }} />
      </Button>
    </div>
  )

  const openChangeStatusModal = (id: string, status: string) => {

    const params = {
      id,
      status: getNextStatusValue(status, 'status')
    }
    setChangeStatusMessage(params.status === locationsStatus.inactive ? 'The selected location and related devices and cashiers will be deactivated. Are you sure?' : 'The selected location will change. Are you sure?')
    setParamsToChangeStatus(params)
    setModalVisible(true)
  }

  const changeLocationStatus = async () => {
    loading.addLoading()
    await updateLocationStatus(paramsToChangeStatus)
    await onRefresh()
    setModalVisible(false)
    loading.removeLoading()
  }


  return (
    <div className="charities">
      <Modal 
        title={changeStatusMessage}
        open={modalVisible}
        onOk={changeLocationStatus} 
        onCancel={() => setModalVisible(false)}
      />
      <Modal 
        title={locationToPreview.name}
        open={previewModal}
        onOk={() => setPreviewModal(false)} 
        onCancel={() => setPreviewModal(false)} 
        centered
      >
        <div className="charities__preview">
          <p><strong>Name:</strong> {locationToPreview.name || '--'}</p>
          <p><strong>Type:</strong> {locationToPreview.locationType || '--'}</p>
          <p><strong>Address:</strong> {locationToPreview.address || '--'}</p>
          <p><strong>Zipcode:</strong> {locationToPreview.zipcode || '--'}</p>
          <p><strong>City:</strong> {locationToPreview.city || '--'}</p>
          <p><strong>State:</strong> {locationToPreview.state || '--'}</p>
          <p><strong>Emails:</strong> {locationToPreview.email || '--'}</p>
          <p><strong>Premises Permit:</strong> {locationToPreview.permit || '--'}</p>
          <p><strong>License:</strong> {locationToPreview.license || '--'}</p>
          <p><strong>Devices Limit:</strong> {locationToPreview.devicesLimit || '--'}</p>
          <p><strong>Status:</strong> {locationToPreview.status || '--'}</p>
          <p><strong>Charity:</strong> {locationToPreview.charityName || '--'}</p>
          <p><strong>Distributor:</strong> {locationToPreview.distributorName || '--'}</p>
          <p><strong>Seasonal:</strong> {String(locationToPreview.seasonal) || '--'}</p>
          <p><strong>Server:</strong> {locationToPreview.serverAlias || '--'}</p>
          <p><strong>Drawer Enabled:</strong> {locationToPreview.drawerEnabled || '--'}</p>
          <p><strong>POS Bundle Server:</strong> {locationToPreview.posBundleServerEnabled || '--'}</p>
          <p><strong>Batch Deck Management:</strong> {locationToPreview.posBundleServerEnabled || '--'}</p>
          <p><strong>Single Use Devices:</strong> {String(locationToPreview.allowSingleUse) || '--'}</p>
          <p><strong>Dynamic Bundle Download:</strong> {String(locationToPreview.dynamicBundleDownload) || '--'}</p>
          <p><strong>Enable Site Inactive Check:</strong> {String(locationToPreview.checkSiteInactive) || '--'}</p>
          {locationToPreview.locationSpecificLobbyOrder !== null && <p><strong>Enable Location Specific Lobby Order:</strong> {String(locationToPreview.locationSpecificLobbyOrder) || '--'}</p>}
          <p><strong>Created Date:</strong> {String(locationToPreview.createdDate) || '--'}</p>
        </div>

      </Modal>
      <Head title="All locations" buttonText="New Location" buttonAction={() => history('/locations/new')} disabled={!canEdit}/>
      <meta name="viewport" content="width=device-width, initial-scale=.75"/>
      <ActionArea buttonAction={onRefresh} buttonText={'Search'} disabled={!validSearch}>
        <div className="cashiers__action-area">
          <SmartSearch
            value={search}
            onChange={(e: string) => setSearch(e)}
            triggerWith={record?.name || ''}
            onValidSearch={setValidSearch}
            label='Smart Search'
            status="active,closed,inactive"
            searchBy={['distributor', 'charity', 'location']}
            onObjValueChange={(e: any) => setParams({...params, ...e})}
            autoFocus
          />
          <SelectInput 
            options={selectOptions} 
            label='Status'
            onSelectChange={(e) => setParams({...params, status: e?.value ? e.value : e})}
            value={params.status}
          />
        </div>
      </ActionArea>
      <List
        rows={locationsList.list}
        columns={[
          {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (value: string) => value,
            sorter: (a: any, b: any) => stringCompare(a.name, b.name),
            width: 100,
          },
          {
            title: 'Type',
            dataIndex: 'locationType',
            key: 'locationType',
            render: (value: string) => value,
            sorter: (a: any, b: any) => stringCompare(a.locationType, b.locationType),
            width: 100
          },
          {
            title: 'City',
            dataIndex: 'city',
            key: 'city',
            render: (value: string) => value,
            sorter: (a: any, b: any) => stringCompare(a.city, b.city),
            width: 100
          },
          {
            title: 'State',
            dataIndex: 'state',
            key: 'state',
            render: (value: string) => value,
            sorter: (a: any, b: any) => stringCompare(a.state, b.state),
            width: 75
          },
          {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone',
            render: (value: string) => value,
            sorter: (a: any, b: any) => stringCompare(a.phone, b.phone),
            width: 100
          },
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (value: string) => <TableStatus status={value} />,
            sorter: (a: any, b: any) => stringCompare(a.status, b.status),
            width: 150
          },
          {
            title: 'Charity',
            dataIndex: 'charityName',
            key: 'charityName',
            render: (value: string) => value,
            sorter: (a: any, b: any) => stringCompare(a.charityName, b.charityName),
            width: 100
          },
          {
            title: 'Distributor',
            dataIndex: 'distributorName',
            key: 'distributorName',
            render: (value: string) => value,
            sorter: (a: any, b: any) => stringCompare(a.distributorName, b.distributorName),
            width: 100
          },
          {
            title: 'Drawer Enabled',
            dataIndex: 'drawerEnabled',
            key: 'drawerEnabled',
            render: (value: string) => getStatusLabel(value),
            sorter: (a: any, b: any) => stringCompare(a.drawerEnabled, b.drawerEnabled),
            width: 100
          },
          {
            title: 'POS Bundle Server',
            dataIndex: 'posBundleServerEnabled',
            key: 'posBundleServerEnabled',
            render: (value: string) => getStatusLabel(value),
            sorter: (a: any, b: any) => stringCompare(a.posBundleServerEnabled, b.posBundleServerEnabled),
            width: 100
          },
          {
            title: 'Actions',
            dataIndex: '',
            key: '',
            render: (value: any) => getActionButtons(value),
            width: 200
          },
        ]}
      />
    </div>
  )
}

export default Locations
